import React, { useCallback, ReactElement } from 'react';
import { useNavigate, useLocation } from 'react-router';
import PageTabs from '../../../../../components/page-tabs/page-tabs.component';
import Card from '../../../../../components/card/card.component';
import SettingsUserNotificationsEmail
  from './settings-user-email-notifications/settings-user-email-notifications.component';
import SettingsUserInternalNotifications
  from './settings-user-internal-notifications/settings-user-internal-notifications.component';
import SettingsUserInternalComments from './settings-user-internal-comments/settings-user-internal-comments.component';
import FormatService from '../../../../../services/format-service';
import {
  COMMENT_LIST_VIEW, NOTIFICATION_LIST_VIEW,
} from '../../../../../constants/entity/permissions';
import {
  TAB_NAME_EMAIL_NOTIFICATIONS, TAB_NAME_INTERNAL_COMMENTS, TAB_NAME_INTERNAL_NOTIFICATIONS,
} from '../../../../../constants/system/tab-names';
import { QUERY_PARAM_TAB } from '../../../../../constants/system/query-params';
import { ROUTE_SETTINGS_NOTIFICATIONS } from '../../../../../constants/system/routes';

const SettingsUserNotifications: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isFormChanged,
    isCurrentUser,
    formError,
    notificationSettingsData,
    notificationTypeSettingsData,
    commentTypeSettingsData,
    dealList,
    portfolioList,
    selectedDeals,
    selectedPortfolios,
    visibleOptions,
    onCancelForm,
    onSubmitForm,
    onOptionDealsChange,
    onOptionPortfoliosChange,
    onVisibleOptionsChange,
    onDealSearch,
    onDealSearchClose,
    onBeforeLocationChange,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_EMAIL_NOTIFICATIONS,
      title: 'Email Notifications',
      component: (
        <SettingsUserNotificationsEmail
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          notificationSettingsData={notificationSettingsData}
          selectedDeals={selectedDeals}
          selectedPortfolios={selectedPortfolios}
          formError={formError}
          dealList={dealList}
          portfolioList={portfolioList}
          visibleOptions={visibleOptions}
          onOptionDealsChange={onOptionDealsChange}
          onOptionPortfoliosChange={onOptionPortfoliosChange}
          onVisibleOptionsChange={onVisibleOptionsChange}
          onDealSearch={onDealSearch}
          onDealSearchClose={onDealSearchClose}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_INTERNAL_NOTIFICATIONS,
      title: 'Internal Notifications',
      isVisible: isCurrentUser,
      permission: NOTIFICATION_LIST_VIEW,
      component: (
        <SettingsUserInternalNotifications
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          notificationTypeSettingsData={notificationTypeSettingsData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_INTERNAL_COMMENTS,
      title: 'Internal Comments',
      isVisible: isCurrentUser,
      permission: COMMENT_LIST_VIEW,
      component: (
        <SettingsUserInternalComments
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          commentTypeSettingsData={commentTypeSettingsData}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: ROUTE_SETTINGS_NOTIFICATIONS,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs]);

  const tabItemEmailNotifications: TabItem | null = tabs && tabs.length ? tabs.find((tab: TabItem): boolean => (
    tab.name === TAB_NAME_EMAIL_NOTIFICATIONS
  )) || null : null;
  const tabItemEmailNotificationsComponent: ReactElement | null = tabItemEmailNotifications
  && tabItemEmailNotifications.component ? tabItemEmailNotifications.component : null;

  return (
    <Card isContainer isFullWidth withNoPadding>
      {
        isCurrentUser ? (
          <PageTabs
            isDisabled={isFormChanged}
            isCompressed={isRightDrawerOpen}
            tabs={tabs}
            currentTab={queryTab}
            onTabChange={onTabChange}
            onBeforeLocationChange={onBeforeLocationChange}
          />
        ) : (
          tabItemEmailNotificationsComponent
        )
      }
    </Card>
  );
});

export default SettingsUserNotifications;
